import { useState, useEffect } from "react";
import { trpc } from "@/lib/trpc";
import { usePermissions } from "@/hooks/usePermissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";

const EMPTY_FORM = {
  phoneNumber: "",
  name: "",
  email: "",
  contactType: "public_admin",
  companyName: "",
  companyType: "",
  sector: "",
  jobTitle: "",
  city: "",
  state: "",
  notes: "",
};

/**
 * Cadastro de contato dentro do Atendimento — cria um contato novo ou completa
 * o da conversa aberta.
 *
 * O modo de edição existe porque todo inbound já nasce com contato: o webhook
 * cria a linha com `name` = o próprio telefone. O painel lateral só oferecia
 * "Adicionar Contato" quando o contato NÃO existia, ou seja, nunca — e o
 * atendente não tinha como registrar o nome de quem estava falando sem sair
 * para a tela de Contatos e procurar pelo número.
 *
 * Telefone e e-mail chegam MASCARADOS de `contacts.get` para quem não tem
 * `data:phone_visible` / `data:email_visible` (ver `_core/pii-mask.ts`). Por
 * isso os campos ficam bloqueados nesse caso: preencher o form com "5511****71"
 * e salvar gravaria a máscara por cima do número real. O servidor descarta os
 * dois campos pelo mesmo motivo — aqui é só para o usuário entender o porquê.
 */
export default function AddContactDialog({
  open,
  onClose,
  initialPhone,
  initialName,
  contactId,
}: {
  open: boolean;
  onClose: () => void;
  initialPhone: string | null;
  initialName: string | null;
  /** Quando presente, o diálogo edita este contato em vez de criar um novo. */
  contactId?: number | null;
}) {
  const utils = trpc.useUtils();
  const { can } = usePermissions();
  const isEdit = contactId != null;
  const canSeePhone = can("data:phone_visible");
  const canSeeEmail = can("data:email_visible");

  const [form, setForm] = useState(EMPTY_FORM);

  const { data: contact, isLoading: loadingContact } = trpc.contacts.get.useQuery(
    { id: contactId ?? 0 },
    { enabled: open && isEdit },
  );

  const invalidate = () => {
    utils.contacts.list.invalidate();
    utils.inbox.listConversations.invalidate();
    if (isEdit) utils.contacts.get.invalidate({ id: contactId! });
  };

  const createMut = trpc.contacts.create.useMutation({
    onSuccess: () => {
      invalidate();
      toast.success("Contato adicionado com sucesso!");
      onClose();
    },
    onError: (e) => toast.error(toUserFriendlyError(e, "Erro ao adicionar contato")),
  });

  const updateMut = trpc.contacts.update.useMutation({
    onSuccess: () => {
      invalidate();
      toast.success("Contato atualizado!");
      onClose();
    },
    onError: (e) => toast.error(toUserFriendlyError(e, "Erro ao atualizar contato")),
  });

  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      if (!contact) return;
      setForm({
        // Campo mascarado não volta para o form: o que o usuário não pode ver
        // ele também não edita, e um "" aqui nunca vira apagamento porque o
        // campo bloqueado fica fora do payload.
        phoneNumber: canSeePhone ? (contact.phoneNumber ?? "") : "",
        name: contact.name ?? "",
        email: canSeeEmail ? (contact.email ?? "") : "",
        contactType: (contact as any).contactType ?? "public_admin",
        companyName: contact.companyName ?? "",
        companyType: contact.companyType ?? "",
        sector: (contact as any).sector ?? "",
        jobTitle: (contact as any).jobTitle ?? "",
        city: contact.city ?? "",
        state: contact.state ?? "",
        notes: contact.notes ?? "",
      });
    } else {
      setForm({
        ...EMPTY_FORM,
        phoneNumber: initialPhone ?? "",
        name: initialName ?? "",
      });
    }
  }, [open, isEdit, contact, initialPhone, initialName, canSeePhone, canSeeEmail]);

  const isPending = createMut.isPending || updateMut.isPending;

  const handleSave = () => {
    if (!form.name.trim()) {
      toast.error("Nome é obrigatório");
      return;
    }
    // Telefone é opcional — órgão que a equipe só tem por e-mail entra do
    // mesmo jeito. Se veio preenchido, precisa ser um número plausível.
    const digits = form.phoneNumber.replace(/[^0-9]/g, "");
    if (canSeePhone && form.phoneNumber.trim() && digits.length < 8) {
      toast.error("Telefone parece inválido. Apague ou corrija.");
      return;
    }

    const comum = {
      name: form.name,
      companyName: form.companyName || null,
      companyType: form.companyType || null,
      city: form.city || null,
      state: form.state || null,
      notes: form.notes || null,
      contactType: form.contactType as any,
      sector: form.sector || null,
      jobTitle: form.jobTitle || null,
      ...(canSeePhone ? { phoneNumber: digits || null } : {}),
      ...(canSeeEmail ? { email: form.email || null } : {}),
    };

    if (isEdit) updateMut.mutate({ id: contactId!, ...comum } as any);
    else createMut.mutate(comum as any);
  };

  const isPublicAdmin = form.contactType === "public_admin";

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="w-[95vw] max-w-lg max-h-[90vh] overflow-y-auto mx-2 sm:mx-0">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Editar Contato" : "Adicionar Contato"}</DialogTitle>
        </DialogHeader>
        {isEdit && loadingContact ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary" />
          </div>
        ) : (
        <div className="grid grid-cols-2 gap-3 py-2">
          <div className="col-span-2">
            <Label>Tipo de Contato</Label>
            <select
              className="w-full mt-1 px-3 py-2 border border-border rounded-md text-sm bg-background"
              value={form.contactType}
              onChange={(e) => setForm(f => ({ ...f, contactType: e.target.value }))}
            >
              <option value="public_admin">Administração Pública</option>
              <option value="empresa">Empresa</option>
              <option value="cliente_final">Cliente Final</option>
            </select>
          </div>
          <div className="col-span-2">
            <Label>Telefone (com DDI)</Label>
            <Input
              placeholder={canSeePhone ? "5511999999999 (opcional)" : "Sem permissão para ver"}
              value={form.phoneNumber}
              disabled={!canSeePhone}
              onChange={(e) => setForm(f => ({ ...f, phoneNumber: e.target.value }))}
              className="mt-1"
            />
            <p className="text-xs text-muted-foreground mt-1">
              {canSeePhone
                ? "Sem telefone o contato não recebe WhatsApp, mas fica no CRM com cidade, órgão e cargo."
                : "Seu perfil não tem permissão para ver ou alterar o telefone."}
            </p>
          </div>
          <div className="col-span-2">
            <Label>Nome *</Label>
            <Input
              placeholder="Nome completo"
              value={form.name}
              onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
              className="mt-1"
            />
          </div>
          <div className="col-span-2">
            <Label>E-mail</Label>
            <Input
              placeholder={canSeeEmail ? "email@exemplo.com" : "Sem permissão para ver"}
              value={form.email}
              disabled={!canSeeEmail}
              onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
              className="mt-1"
            />
          </div>
          {isPublicAdmin ? (
            <>
              <div className="col-span-2">
                <Label>Órgão (Prefeitura, Câmara, Autarquia...)</Label>
                <Input
                  placeholder="Ex: Prefeitura Municipal de Campinas"
                  value={form.companyName}
                  onChange={(e) => setForm(f => ({ ...f, companyName: e.target.value }))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Tipo de Órgão</Label>
                <select
                  className="w-full mt-1 px-3 py-2 border border-border rounded-md text-sm bg-background"
                  value={form.companyType}
                  onChange={(e) => setForm(f => ({ ...f, companyType: e.target.value }))}
                >
                  <option value="">Selecione...</option>
                  <option value="Prefeitura">Prefeitura</option>
                  <option value="Câmara">Câmara Municipal</option>
                  <option value="Autarquia">Autarquia</option>
                  <option value="Governo Estadual">Governo Estadual</option>
                  <option value="Assembleia Legislativa">Assembleia Legislativa</option>
                  <option value="Tribunal">Tribunal</option>
                  <option value="Outro">Outro</option>
                </select>
              </div>
              <div>
                <Label>Setor / Secretaria</Label>
                <Input
                  placeholder="Ex: Sec. de Administração"
                  value={form.sector}
                  onChange={(e) => setForm(f => ({ ...f, sector: e.target.value }))}
                  className="mt-1"
                />
              </div>
            </>
          ) : (
            <div className="col-span-2">
              <Label>Empresa</Label>
              <Input
                placeholder="Nome da empresa"
                value={form.companyName}
                onChange={(e) => setForm(f => ({ ...f, companyName: e.target.value }))}
                className="mt-1"
              />
            </div>
          )}

          {/* Cargo — da pessoa, vale para os dois ramos acima */}
          <div className="col-span-2">
            <Label>Cargo</Label>
            <Input
              placeholder="Ex: Pregoeiro, Diretor de Compras"
              value={form.jobTitle}
              onChange={(e) => setForm(f => ({ ...f, jobTitle: e.target.value }))}
              className="mt-1"
            />
          </div>
          <div>
            <Label>Cidade</Label>
            <Input
              placeholder="Cidade"
              value={form.city}
              onChange={(e) => setForm(f => ({ ...f, city: e.target.value }))}
              className="mt-1"
            />
          </div>
          <div>
            <Label>UF</Label>
            <Input
              placeholder="SP"
              maxLength={2}
              value={form.state}
              onChange={(e) => setForm(f => ({ ...f, state: e.target.value.toUpperCase() }))}
              className="mt-1"
            />
          </div>
          <div className="col-span-2">
            <Label>Observações</Label>
            <Input
              placeholder="Notas internas..."
              value={form.notes}
              onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
              className="mt-1"
            />
          </div>
        </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSave} disabled={isPending || (isEdit && loadingContact)}>
            {isPending ? "Salvando..." : "Salvar Contato"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
